import { loadStripe } from '@stripe/stripe-js';
import type { BuildIntake, BuildQuoteResult, ConversationTurn } from './types';

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
);

export type BuildPaymentType = 'deposit' | 'full';

export async function fetchBuildQuote(
  intake: BuildIntake,
  negotiationRound = 0,
  conversationHistory?: ConversationTurn[]
): Promise<{ intake: BuildIntake; quote: BuildQuoteResult; source?: 'llm' | 'rules' }> {
  const res = await fetch('/.netlify/functions/generate-build-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ intake, negotiationRound, conversationHistory }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not generate quote');
  return data;
}

export async function analyzeBuildIntake(
  conversationHistory: ConversationTurn[],
  currentIntake?: BuildIntake
): Promise<{
  intake: Partial<BuildIntake>;
  patch: Partial<BuildIntake>;
  readyForQuote: boolean;
  missingFields: string[];
}> {
  const res = await fetch('/.netlify/functions/analyze-build-intake', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationHistory, currentIntake }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not analyze conversation');
  return data;
}

export async function saveBuildQuote(
  intake: BuildIntake,
  quote: BuildQuoteResult,
  conversationHistory?: ConversationTurn[]
): Promise<{
  id: string;
  shareUrl: string;
  intake: BuildIntake;
  quote: BuildQuoteResult;
}> {
  const res = await fetch('/.netlify/functions/save-build-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      intake,
      quote,
      negotiationRound: quote.negotiationRound,
      conversationHistory,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not save quote');
  return data;
}

export async function fetchSavedQuote(id: string) {
  const res = await fetch(`/.netlify/functions/get-build-quote?id=${encodeURIComponent(id)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Quote not found');
  return data as {
    id: string;
    intake: BuildIntake;
    quote: BuildQuoteResult;
    negotiationRound: number;
  };
}

export async function negotiateSavedQuote(
  quoteId: string,
  options?: {
    clientMessage?: string;
    conversationHistory?: ConversationTurn[];
  }
) {
  const res = await fetch('/.netlify/functions/negotiate-build-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quoteId,
      clientMessage: options?.clientMessage,
      conversationHistory: options?.conversationHistory,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not update quote');
  return data as {
    quote: BuildQuoteResult;
    atMaxDiscount: boolean;
    firmResponse: boolean;
  };
}

export async function createBuildCheckoutSession(
  quoteId: string,
  paymentType: BuildPaymentType = 'deposit',
  options?: { linkOnly?: boolean }
): Promise<{
  sessionId: string;
  url: string;
  amountCents: number;
  paymentType: BuildPaymentType;
}> {
  const res = await fetch('/.netlify/functions/create-build-checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quoteId,
      paymentType,
      linkOnly: options?.linkOnly ?? false,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Checkout failed');
  return data;
}

export async function openBuildCheckout(
  quoteId: string,
  paymentType: BuildPaymentType = 'deposit'
) {
  const stripe = await stripePromise;
  if (!stripe) throw new Error('Stripe failed to load');

  const data = await createBuildCheckoutSession(quoteId, paymentType);
  const result = await stripe.redirectToCheckout({ sessionId: data.sessionId });
  if (result.error) throw new Error(result.error.message);
}

/** Parse freeform notes into conversation turns for the quote LLM. */
export function parseConversationNotes(notes: string): ConversationTurn[] {
  if (!notes.trim()) return [];
  return notes
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^(client|user|milan|assistant):\s*(.+)$/i);
      if (match) {
        const role = /^(client|user)$/i.test(match[1]) ? 'user' : 'assistant';
        return { role, content: match[2].trim() };
      }
      return { role: 'user' as const, content: line };
    });
}
